/**
 * Firebase 初期化/ユーティリティ（Auth/Firestore/Google OAuth トークン取得）。
 */
"use client";

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  browserPopupRedirectResolver, // ★追加
  onAuthStateChanged,
  User,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyCqt5C_-IPYqQTmEmoHckpI_XSw1gtdr18",
  authDomain: "miacis-472702.firebaseapp.com",
  projectId: "miacis-472702",
  storageBucket: "miacis-472702.firebasestorage.app",
  messagingSenderId: "1022593687854",
  appId: "1:1022593687854:web:85f4436c63291c06919abf",
};

// 重複初期化防止
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// 単一インスタンスを強制
export const auth = getAuth(app);

// Provider も共有（毎回 new しない）
export const googleProvider = new GoogleAuthProvider();
// ★毎回アカウント選択画面を表示（Windows Chrome でも強制表示）
// consent を併用すると同意画面を挟むため、無言サインインを抑止できる
googleProvider.setCustomParameters({ 
  prompt: 'select_account consent' 
});

// Googleアクセストークンを取得する関数（Popup方式）
export async function getGoogleAccessToken(): Promise<string> {
  const provider = new GoogleAuthProvider();
  provider.addScope('https://www.googleapis.com/auth/drive.file');
  provider.addScope('https://www.googleapis.com/auth/documents');
  // ★こちらもアカウント選択画面を表示（consent併用）
  provider.setCustomParameters({ prompt: 'select_account consent' });

  // Popup でサインイン（追加スコープ付き）
  const result = await signInWithPopup(auth, provider);
  
  // アクセストークンを取得
  const credential = GoogleAuthProvider.credentialFromResult(result);
  const accessToken = credential?.accessToken;
  
  if (!accessToken) {
    throw new Error('Failed to get Google access token');
  }
  
  return accessToken;
}

// ★追加：Popup実行中フラグ（親タブの先走り遷移を抑止）
let popupInProgress = false;

// ★修正：iOS Chrome (CriOS) 判定を追加
const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isCriOS = typeof navigator !== "undefined" && /CriOS\/\d+/i.test(navigator.userAgent);
const isMacChrome = typeof navigator !== "undefined" &&
  /Mac OS X/.test(navigator.userAgent) &&
  /Chrome\/\d+/.test(navigator.userAgent);

// ログイン関数（Popup のみ、動いているアプリと同じ）
export async function login(): Promise<User> {
  const provider = googleProvider;
  
  // すべてのブラウザとデバイスでデフォルトの browserLocalPersistence を使用
  try {
    console.log('[auth] setting persistence: LOCAL (default)');
    await setPersistence(auth, browserLocalPersistence);
  } catch (e: any) {
    console.warn('[auth] persistence failed:', e.message);
    throw e;
  }
  
  // ★追加：Popup実行中フラグ + 安全タイマー（10秒で自動解除）
  popupInProgress = true;
  const safetyTimer = setTimeout(() => {
    console.warn('[auth] safety timer: clearing popup flag after 10s');
    popupInProgress = false;
  }, 10000);
  
  try {
    console.log('[auth] attempting popup signin with explicit resolver');
    const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    console.log('[auth] popup signin success:', result.user.uid);
    return result.user;
  } finally {
    clearTimeout(safetyTimer);
    popupInProgress = false;
    console.log('[auth] popup completed, flag cleared');
  }
}

// ★追加：Popup実行中かどうかを参照する関数
export function isPopupRunning(): boolean {
  return popupInProgress;
}

// ログアウト関数
export async function logout(): Promise<void> {
  await signOut(auth);
  console.log('[auth] user signed out');
}

// Auth初期化を待つヘルパー
export async function waitForUser(timeoutMs = 8000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    let done = false;
    
    const unsub = onAuthStateChanged(auth, (u) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsub();
      resolve(u);
    });
    
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      unsub();
      resolve(null);
    }, timeoutMs);
  });
}

// 認証ヘッダーを取得
export async function getAuthHeaders() {
  const user = await waitForUser();               // ★ Auth初期化を待つ
  if (!user) throw new Error("User not authenticated");
  const idToken = await user.getIdToken(true);    // 強制リフレッシュ気味に
  return { Authorization: `Bearer ${idToken}` };
}

// 認証付きfetch関数（401エラー対策）
export async function authedFetch(input: RequestInfo, init: RequestInit = {}) {
  const user = await waitForUser(); // ★ 待機を追加
  const headers = new Headers(init.headers || {});

  // 認証されていない場合は401をスロー
  if (!user) {
    throw new Error("User not authenticated");
  }

  let token = await user.getIdToken().catch(() => null);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  let res = await fetch(input, { ...init, headers });

  // 401/403 は1回だけ強制リフレッシュで再試行
  if ((res.status === 401 || res.status === 403) && user) {
    token = await user.getIdToken(true).catch(() => null);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      res = await fetch(input, { ...init, headers });
    }
  }

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  return res;
}

export const db = getFirestore(app, 'miacis-firestore');